import { ReportIds } from 'ag-common-lib/lib/contracts/reporting-api.contracts';

export type ReportPeriod = 'ytd' | 'weekly' | 'monthly' | 'quarterly' | 'custom';
export interface ReportDateRange {
  startDate: string;
  endDate: string;
}
export interface ReportPeriodSelection extends ReportDateRange {
  period: ReportPeriod;
  periodEnding: string;
}
export interface ReportPeriodOption {
  value: string;
  description: string;
}
export const REPORT_PERIODS: { value: ReportPeriod; description: string }[] = [
  { value: 'ytd', description: 'Year to date' },
  { value: 'weekly', description: 'Weekly' },
  { value: 'monthly', description: 'Monthly' },
  { value: 'quarterly', description: 'Quarterly' },
  { value: 'custom', description: 'Custom' },
];
export const AVAILABILITY_ERROR = 'Report data availability could not be determined. Please try again later.';
const PAID_IDS = new Set<string>([ReportIds.mgaPaid, ReportIds.personalPaid, ReportIds.rmdPaid]);
export function isPaidReport(reportId: string): boolean {
  return PAID_IDS.has(reportId);
}
export function isReportPeriod(value: unknown): value is ReportPeriod {
  return REPORT_PERIODS.some(p => p.value === value);
}

/** Calendar dates are UTC anchored; only the browser's calendar date is read in local time. */
export function dateOnly(value: unknown): string | null {
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return null;
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && iso(date) === value ? value : null;
}
export function todayDate(): string {
  return dateOnly(new Date())!;
}
function utc(value: string): Date {
  return new Date(`${value}T12:00:00Z`);
}
function iso(value: Date): string {
  return value.toISOString().slice(0, 10);
}
function addDays(value: string, count: number): string {
  const d = utc(value);
  d.setUTCDate(d.getUTCDate() + count);
  return iso(d);
}
function monthEnd(year: number, month: number): string {
  return iso(new Date(Date.UTC(year, month + 1, 0, 12)));
}
function shiftMonths(value: string, months: number): string {
  const d = utc(value);
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1, 12));
  target.setUTCDate(
    Math.min(d.getUTCDate(), utc(monthEnd(target.getUTCFullYear(), target.getUTCMonth())).getUTCDate()),
  );
  return iso(target);
}
export function formatReportDate(value: string): string {
  return dateOnly(value) ? `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}` : '';
}
export function latestPeriodEnding(period: ReportPeriod, boundary: string): string {
  if (!dateOnly(boundary) || period === 'custom') return '';
  if (period === 'ytd') return boundary;
  const d = utc(boundary);
  if (period === 'weekly') return addDays(boundary, -((d.getUTCDay() - 4 + 7) % 7));
  const month = period === 'monthly' ? d.getUTCMonth() : Math.floor(d.getUTCMonth() / 3) * 3 + 2;
  const end = monthEnd(d.getUTCFullYear(), month);
  return end <= boundary ? end : monthEnd(d.getUTCFullYear(), month - (period === 'monthly' ? 1 : 3));
}
function weekNumber(value: string): number {
  const d = utc(value);
  d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const first = new Date(Date.UTC(d.getUTCFullYear(), 0, 4, 12));
  first.setUTCDate(first.getUTCDate() + 3 - ((first.getUTCDay() + 6) % 7));
  return 1 + Math.round((d.getTime() - first.getTime()) / 604800000);
}
export function buildReportPeriodOptions(
  period: ReportPeriod,
  through: string | null,
  today = todayDate(),
  from?: string | null,
): ReportPeriodOption[] {
  if (!dateOnly(through) || !dateOnly(today) || period === 'custom') return [];
  const boundary = through! < today ? through! : today;
  // Without coverage-start metadata, do not invent historical dataset coverage.
  if (period === 'ytd') {
    const options: ReportPeriodOption[] = [];
    for (let end = boundary; end >= `${boundary.slice(0, 4)}-01-01`; end = addDays(end, -1))
      options.push({ value: end, description: formatReportDate(end) });
    return options;
  }
  const first = dateOnly(from) ?? `${boundary.slice(0, 4)}-01-01`;
  const options: ReportPeriodOption[] = [];
  let end = latestPeriodEnding(period, boundary);
  while (end && end >= first) {
    const d = utc(end);
    const description =
      period === 'weekly'
        ? `Week ${weekNumber(end)} — ${formatReportDate(addDays(end, -6))} to ${formatReportDate(end)}`
        : period === 'monthly'
          ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
          : `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`;
    options.push({ value: end, description });
    end =
      period === 'weekly'
        ? addDays(end, -7)
        : monthEnd(d.getUTCFullYear(), d.getUTCMonth() - (period === 'monthly' ? 1 : 3));
  }
  return options;
}
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
export function reportPeriodRange(paid: boolean, period: ReportPeriod, end: string): ReportDateRange | null {
  if (!dateOnly(end) || period === 'custom') return null;
  let startDate: string;
  if (period === 'ytd') startDate = `${end.slice(0, 4)}-01-01`;
  else if (period === 'weekly') startDate = addDays(end, -6);
  else if (paid) {
    const date = utc(end);
    const month = period === 'monthly' ? date.getUTCMonth() : Math.floor(date.getUTCMonth() / 3) * 3;
    startDate = iso(new Date(Date.UTC(date.getUTCFullYear(), month, 1, 12)));
  } else startDate = addDays(shiftMonths(end, period === 'monthly' ? -1 : -3), 1);
  return { startDate, endDate: end };
}
export function selectReportPeriod(paid: boolean, period: ReportPeriod, end = ''): ReportPeriodSelection {
  const range = reportPeriodRange(paid, period, end);
  return {
    period,
    periodEnding: period === 'custom' ? '' : end,
    startDate: range?.startDate ?? '',
    endDate: range?.endDate ?? '',
  };
}
export function reportRangeErrors(range: ReportDateRange, through: string | null): string[] {
  if (!dateOnly(through)) return [AVAILABILITY_ERROR];
  const errors: string[] = [];
  if (!dateOnly(range.startDate)) errors.push('Start Date is required.');
  if (!dateOnly(range.endDate)) errors.push('End Date is required.');
  if (dateOnly(range.startDate) && dateOnly(range.endDate) && range.startDate > range.endDate)
    errors.push('Start Date cannot be after End Date.');
  if (
    (dateOnly(range.startDate) && range.startDate > through!) ||
    (dateOnly(range.endDate) && range.endDate > through!)
  )
    errors.push(`Data is only available through ${formatReportDate(through!)}.`);
  return errors;
}
export function inferReportPeriod(paid: boolean, start: string, end: string): ReportPeriod {
  for (const period of ['ytd', 'weekly', 'monthly', 'quarterly'] as const) {
    if (latestPeriodEnding(period, end) === end && reportPeriodRange(paid, period, end)?.startDate === start)
      return period;
  }
  return 'custom';
}
export function resolveReportPeriod(
  paid: boolean,
  through: string | null,
  input: { period?: unknown; periodEnding?: unknown; startDate?: unknown; endDate?: unknown },
  from?: string | null,
  today = todayDate(),
): ReportPeriodSelection {
  const start = dateOnly(input.startDate) ?? '';
  const end = dateOnly(input.endDate) ?? '';
  const hasDateInput = [input.startDate, input.endDate].some(
    value => value !== undefined && value !== null && String(value).trim() !== '',
  );
  if ((!isReportPeriod(input.period) || input.period === 'custom') && !hasDateInput) {
    const ending = buildReportPeriodOptions('weekly', through, today, from)[0]?.value ?? '';
    const range = reportPeriodRange(paid, 'weekly', ending);
    return { period: 'custom', periodEnding: '', startDate: range?.startDate ?? '', endDate: range?.endDate ?? '' };
  }
  const period = isReportPeriod(input.period)
    ? input.period
    : start && end
      ? inferReportPeriod(paid, start, end)
      : hasDateInput
        ? 'custom'
        : 'custom';
  if (period === 'custom') return { period, periodEnding: '', startDate: start, endDate: end };
  const options = buildReportPeriodOptions(period, through, today, from);
  const requestedEnd = dateOnly(input.periodEnding) ?? (end || null);
  const selectedEnd = options.some(o => o.value === requestedEnd) ? requestedEnd! : (options[0]?.value ?? '');
  return selectReportPeriod(paid, period, selectedEnd);
}
export function reportAvailabilityMessage(period: ReportPeriod, through: string | null, today = todayDate()): string {
  if (!dateOnly(through)) return AVAILABILITY_ERROR;
  const label = `Data available through ${formatReportDate(through!)}.`;
  if (period === 'ytd') return through! < today ? `${label} The latest reporting period is not yet available.` : label;
  const date = utc(today);
  const currentEnd =
    period === 'weekly'
      ? addDays(today, (4 - date.getUTCDay() + 7) % 7)
      : monthEnd(
          date.getUTCFullYear(),
          period === 'quarterly' ? Math.floor(date.getUTCMonth() / 3) * 3 + 2 : date.getUTCMonth(),
        );
  return period !== 'custom' && currentEnd > latestPeriodEnding(period, through!)
    ? `${label} The latest reporting period is not yet available.`
    : label;
}
